// Brain SDK — single client used by every module. See spec/22-brain-sdk.md.
//
// M0 status: chat() implemented end-to-end against Worker. Other methods stub
// out — they'll land progressively in M1 phases per the rollout plan.

import { config } from '@/shared/config';
import type {
  BrainError,
  ChatParams,
  ChatResult,
  ComposeParams,
  ComposeResult,
  ExtractParams,
  ExtractResult,
  Fact,
  FlagParams,
  Flag,
  RecallParams,
  RememberParams,
  RemindParams,
  WatcherRunResult,
} from './brain.types';

async function callWorker<T>(path: string, body: unknown, idempotencyKey?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (idempotencyKey) headers['X-Idempotency-Key'] = idempotencyKey;

  let res: Response;
  try {
    res = await fetch(`${config.workerUrl}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  } catch (networkErr) {
    const err: BrainError = {
      type: 'upstream_down',
      message: `Network error reaching Worker: ${(networkErr as Error).message}`,
      retryable: true,
    };
    throw err;
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err: BrainError = {
      type: (data as { errorType?: BrainError['type'] }).errorType ?? 'unknown',
      message: (data as { error?: string }).error ?? `Worker call failed: ${res.status}`,
      retryable: res.status >= 500 || res.status === 429,
      upstreamDetail: (data as { detail?: unknown }).detail,
    };
    throw err;
  }
  return data as T;
}

export const Brain = {
  /**
   * Conversational, multi-turn. Persists threads + messages to Firebase
   * (per spec/10-module-brain.md + spec/20-data-model.md) and passes prior
   * messages back to the Worker so Claude sees full context.
   *
   * If `params.threadId` is omitted, starts a new thread and injects the
   * last 8 messages from prior threads as cross-session context
   * (mirrors jemma-os brain.py:_inject_prior_context).
   */
  async chat(params: ChatParams): Promise<ChatResult> {
    // Load Firebase persistence layer lazily — keeps Firebase out of bundles
    // that don't use Brain.chat persistence (none today, but future-proof).
    const memory = await import('@/shared/brainMemory');

    let threadId = params.threadId;
    let history: Array<{ role: string; content: string }> = [];

    if (threadId) {
      // Continuing an existing thread — load its messages
      const messages = await memory.loadMessages(threadId);
      history = memory.messagesToHistory(messages);
    } else {
      // New thread — pull cross-session context from recent prior threads
      const crossSession = await memory.loadCrossSessionContext(null, 8);
      history = memory.messagesToHistory(crossSession);
      threadId = await memory.createThread(params.message);
    }

    // Persist the user message BEFORE the Worker call so it's saved
    // even if Claude fails (we can retry from this state).
    await memory.appendMessage(threadId, {
      role: 'user',
      content: params.message,
      contextRef: params.contextRef,
    });

    // Worker call — now with history so Claude has context
    const result = await callWorker<ChatResult>(
      '/api/brain/chat',
      { ...params, threadId, history },
      params.idempotencyKey,
    );

    // Persist the assistant response
    await memory.appendMessage(threadId, {
      role: 'assistant',
      content: result.reply,
      tokensIn: result.usage.tokensIn,
      tokensOut: result.usage.tokensOut,
      model: result.usage.model,
      cost: result.usage.cost,
    });

    // Always return the threadId we used (caller can persist in URL/localStorage)
    return { ...result, threadId };
  },

  // ----- Extract: task-dispatched structured extraction. M1.3+ -----

  /**
   * Extract structured data via a named task. The Worker dispatches by
   * `task` to a task-specific handler with its own tool schema. See
   * spec/22-brain-sdk.md "Extract" + worker/src/brain/extract.ts.
   *
   * Supported tasks as of M1.3:
   * - 'rfq_from_email' — input: { sender?, senderEmail?, subject?, sentDate?, body?, attachments? }
   *
   * More tasks land as M1 progresses (document, entity, part, mcmaster_part, etc.).
   */
  async extract<T = unknown>(params: ExtractParams): Promise<ExtractResult<T>> {
    return callWorker<ExtractResult<T>>(
      '/api/brain/extract',
      {
        task: params.task,
        input: params.input,
        modelOverride: params.modelOverride,
      },
      params.idempotencyKey,
    );
  },

  /**
   * Compose an email via Claude, optionally saving the result to Outlook Drafts.
   *
   * Two-step: (1) Worker composes subject + body via Claude tool call,
   * (2) if saveTo='outlook_drafts', the browser side calls Graph to save the draft.
   *
   * The Worker never touches the user's MS access token — that lives in
   * the browser. Per [[feedback-drafts-never-autosend]] this method ONLY
   * composes/drafts. Justin reviews + clicks Send in Outlook.
   */
  async compose(params: ComposeParams): Promise<ComposeResult> {
    interface ComposeWorkerResult {
      subject: string;
      body: string;
      bodyHtml: string;
      to: string;
      cc: string;
      usage: ComposeResult['usage'];
    }

    const composed = await callWorker<ComposeWorkerResult>(
      '/api/brain/compose',
      {
        task: params.task,
        input: params.input,
        modelOverride: params.modelOverride,
      },
      params.idempotencyKey,
    );

    // Default behavior is 'in_app_only' — return composed text and let the caller
    // decide what to do. 'outlook_drafts' triggers the Graph save.
    if (params.saveTo === 'outlook_drafts') {
      // Dynamic import — keeps Graph/MSAL out of bundles that don't need them.
      const { saveToOutlookDrafts } = await import('@/shared/graph');
      const dest = params.destination ?? {};
      const draft = await saveToOutlookDrafts({
        to: dest.to ?? composed.to,
        cc: dest.cc ?? composed.cc,
        bcc: dest.bcc,
        subject: dest.subject ?? composed.subject,
        body: composed.body,
        bodyType: 'Text',
      });
      return {
        subject: composed.subject,
        body: composed.body,
        bodyHtml: composed.bodyHtml,
        draftId: draft.id,
        usage: composed.usage,
      };
    }

    return {
      subject: composed.subject,
      body: composed.body,
      bodyHtml: composed.bodyHtml,
      usage: composed.usage,
    };
  },

  /**
   * Query Jemma's persistent facts memory. Filter by category, key, or
   * key prefix; results sorted by updatedAt (most recent first).
   *
   * See spec/22-brain-sdk.md "Memory (recall + remember)".
   */
  async recall(params: RecallParams): Promise<Fact[]> {
    const memory = await import('@/shared/brainMemory');
    const found = await memory.recallFacts({
      category: params.category,
      key: params.key,
      keyPrefix: params.keyPrefix,
      limit: params.limit,
    });
    return found.map((f) => ({
      id: f.id,
      category: f.category,
      key: f.key,
      value: f.value,
      confidence: f.confidence,
      source: f.source,
      updatedAt: f.updatedAt,
    }));
  },

  /**
   * Write or update a fact in Jemma's persistent memory. Facts are unique
   * on (category, key) — writing the same (category, key) twice UPDATES.
   *
   * Source must be specified: 'user_told' (Justin told her directly),
   * 'extracted_from_doc' (parsed from a document), or 'inferred_from_pattern'
   * (Brain reasoning). Defaults to confidence 1.0 if not specified.
   */
  async remember(params: RememberParams): Promise<{ factId: string }> {
    const memory = await import('@/shared/brainMemory');
    const factId = await memory.rememberFact({
      category: params.category,
      key: params.key,
      value: params.value,
      confidence: params.confidence,
      source: params.source,
      sourceRef: params.sourceRef,
    });
    return { factId };
  },

  /**
   * Create a reminder. Persists to Firebase `/brain/reminders`. See spec/22-brain-sdk.md.
   * `dueAt` is required ISO 8601. Natural-language scheduling lands in M1.4b
   * via Brain.chat tool-use loop.
   */
  async remind(params: RemindParams): Promise<{ reminderId: string }> {
    const memory = await import('@/shared/brainMemory');
    const reminderId = await memory.createReminder({
      text: params.text,
      dueAt: params.dueAt,
      rrule: params.rrule,
      relatedEntity: params.relatedEntity,
    });
    return { reminderId };
  },

  flag: {
    async raise(params: FlagParams): Promise<{ flagId: string }> {
      const memory = await import('@/shared/brainMemory');
      const flagId = await memory.raiseFlag({
        severity: params.severity,
        category: params.category,
        title: params.title,
        body: params.body,
        suggestedAction: params.suggestedAction,
        relatedEntity: params.relatedEntity,
        sourceWatcherId: params.sourceWatcherId,
      });
      return { flagId };
    },
    async acknowledge(flagId: string, opts?: { user?: string }): Promise<void> {
      const memory = await import('@/shared/brainMemory');
      await memory.updateFlagStatus(flagId, 'acknowledged', { user: opts?.user });
    },
    async dismiss(flagId: string, opts?: { user?: string; reason?: string }): Promise<void> {
      const memory = await import('@/shared/brainMemory');
      await memory.updateFlagStatus(flagId, 'dismissed', { user: opts?.user, reason: opts?.reason });
    },
    async snooze(flagId: string, until: string): Promise<void> {
      const memory = await import('@/shared/brainMemory');
      await memory.updateFlagStatus(flagId, 'snoozed', { snoozedUntil: until });
    },
    async list(filter?: Partial<Flag>): Promise<Flag[]> {
      const memory = await import('@/shared/brainMemory');
      const found = await memory.listFlags({
        status: (filter?.status as 'pending' | 'acknowledged' | 'dismissed' | 'snoozed') ?? 'pending',
        severity: filter?.severity,
        category: filter?.category,
      });
      return found.map((f) => ({
        id: f.id,
        severity: f.severity,
        category: f.category,
        title: f.title,
        body: f.body,
        suggestedAction: f.suggestedAction,
        // Cast — Firebase-stored value comes back as plain string; the enum is enforced
        // at write time via FlagParams in the public SDK contract.
        relatedEntity: f.relatedEntity as Flag['relatedEntity'],
        status: f.status,
        createdAt: f.createdAt,
      }));
    },
  },

  /**
   * M1.6 — manually invoke a watcher. In production this same handler also
   * fires on Cloudflare Cron Triggers (configured in worker/wrangler.toml).
   * If the watcher returns a `raiseFlag` payload, this method automatically
   * persists a flag via Brain.flag.raise().
   */
  watchers: {
    async run(watcherId: string): Promise<WatcherRunResult> {
      const res = await fetch(`${config.workerUrl}/api/cron/run/${watcherId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = (await res.json().catch(() => ({}))) as WatcherRunResult & {
        error?: string;
      };
      if (!res.ok) {
        const err: BrainError = {
          type: 'unknown',
          message: data.error ?? `Watcher call failed: ${res.status}`,
          retryable: res.status >= 500 || res.status === 429,
        };
        throw err;
      }

      // If the watcher wants a flag raised, persist it now (client-side).
      // When the Worker fires this same handler from a Cloudflare Cron Trigger
      // (post-deploy), it'll need to write to Firebase itself — that's a M1.6b
      // follow-up (Firebase Admin SDK or REST in the Worker).
      if (data.raiseFlag) {
        try {
          const memory = await import('@/shared/brainMemory');
          await memory.raiseFlag({
            ...data.raiseFlag,
            sourceWatcherId: data.watcherId,
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('Watcher returned raiseFlag but persist failed:', e);
        }
      }
      return data;
    },
  },

  /**
   * Email — read the user's Outlook inbox and draft new emails to specific
   * contacts. Per [[feedback-drafts-never-autosend]] there is NO send method
   * here. Justin reviews every draft in Outlook and clicks Send himself.
   *
   * Registered as Jemma capabilities `email.read` + `email.send_draft`
   * (registry lives in src/shared/jemmaCapabilities.ts, owned by a separate
   * agent — see Outlook-Read task brief).
   */
  email: {
    async readInbox(opts?: {
      top?: number;
      folder?: 'inbox' | 'sentitems';
      search?: string;
    }) {
      const { readInbox } = await import('@/shared/graph');
      return readInbox(opts);
    },
    async readMessage(id: string) {
      const { readMessage } = await import('@/shared/graph');
      return readMessage(id);
    },
    async draftEmail(opts: {
      to: Array<string | { name?: string; email: string }>;
      cc?: Array<string | { name?: string; email: string }>;
      subject: string;
      bodyHtml: string;
      replyToMessageId?: string;
    }) {
      const { draftEmail } = await import('@/shared/graph');
      return draftEmail(opts);
    },
  },

  voice: {
    async startListening(_callback: (transcript: string) => void): Promise<never> {
      const err: BrainError = {
        type: 'unknown',
        message: 'voice_not_enabled — see spec/00-vision.md (voice deferred)',
        retryable: false,
      };
      throw err;
    },
    async speak(_text: string): Promise<never> {
      const err: BrainError = {
        type: 'unknown',
        message: 'voice_not_enabled',
        retryable: false,
      };
      throw err;
    },
    async identifySpeaker(_audio: Blob): Promise<never> {
      const err: BrainError = {
        type: 'unknown',
        message: 'voice_not_enabled',
        retryable: false,
      };
      throw err;
    },
  },
};

export type * from './brain.types';
